import { NavDropDownCloseIcon } from "@theme/assets";
import { AnimatePresence, m } from "framer-motion";
import { useSetAtom } from "jotai";
import { type FC, type ReactNode, useRef } from "react";
import ReactFocusLock from "react-focus-lock";
import { RemoveScroll } from "react-remove-scroll";

import { Box, Container, IconButton, activeNavDropdownAtom } from "@theme/components";

import { useNavHeight } from "@theme/utils";

import type { NavDesktopCategoryDropdownProps } from "@theme/components/Navigation/NavDesktopCategoryDropdown";

interface NavMenuDrawerProps extends Omit<NavDesktopCategoryDropdownProps, "navCategory"> {
  children: ReactNode;
  navCategoryTitle: string;
}

export const NavMenuDrawer: FC<NavMenuDrawerProps> = ({
  buttonRef,
  children,
  navCategoryTitle,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const setActiveMenu = useSetAtom(activeNavDropdownAtom);

  const handleCloseNavigation = () => {
    setActiveMenu(-1);
  };

  const { navHeights } = useNavHeight();

  return (
    <RemoveScroll>
      <AnimatePresence>
        <ReactFocusLock
          returnFocus={() => {
            buttonRef.current?.focus();

            return false;
          }}
        >
          <m.div
            ref={menuRef}
            initial="hide"
            animate="show"
            exit="hide"
            variants={{
              hide: { opacity: 0 },
              show: { opacity: 1 },
            }}
            sx={{
              width: "full",
              position: ["fixed", null, null, "absolute"],
              top: [0, null, null, "100%"],
              left: 0,
              right: 0,
              bg: "backgroundLevel2",
              height: "auto",
              zIndex: 1,
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                handleCloseNavigation();
              }
            }}
            tabIndex={-1}
          >
            <Box
              sx={{
                overflowY: "auto",
                maxHeight: ["100dvh", null, null, `calc(100dvh - ${navHeights[3]}px)`],
                scrollbarGutter: "stable both-edges",
              }}
            >
              <Container
                variant="wideNoGutters"
                sx={{
                  position: "relative",
                  paddingBottom: "env(keyboard-inset, 0px)",
                  paddingX: ["s50", null, "s400", "s700"],
                }}
              >
                <IconButton
                  data-testid="close-modal-button"
                  aria-label={`Close ${navCategoryTitle} Product Dropdown`}
                  sx={{
                    position: "absolute",
                    display: ["none", null, null, "block"],
                    top: "s200",
                    right: "s700",
                    padding: 0,
                    ":hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                  onClick={handleCloseNavigation}
                >
                  <NavDropDownCloseIcon sx={{ color: "goldPrimary" }} />
                </IconButton>
                {children}
              </Container>
            </Box>
          </m.div>
        </ReactFocusLock>
      </AnimatePresence>
    </RemoveScroll>
  );
};
